/*
 * Positions, open and closed.
 *
 * Open ones are marked live off the socket rather than shown at the price the
 * last poll happened to catch. Closed ones are history and never move.
 *
 * Closing by hand is offered here and is never refused by a risk limit, which
 * is deliberate — a limit exists to stop the machine opening something, not to
 * stop a person getting out.
 */

import { useMemo, useState } from "react";

import { useExitPosition, usePositions, useStatus } from "../api/hooks";
import { useLive, useWatch } from "../api/stream";
import type { Position } from "../api/types";
import { Modal } from "../components/Modal";
import { LivePriceCell } from "../components/LivePrice";
import { useToast } from "../components/Toast";
import {
  EXIT_CAPTION,
  clockFromMs,
  clockTime,
  pct,
  price as fmtPrice,
  rupees,
  signedRupees,
  toneOf,
} from "../lib/format";

export function PositionsPage() {
  const status = useStatus();
  const [openOnly, setOpenOnly] = useState(true);
  const positions = usePositions(openOnly, status.data?.marketOpen, 200);
  const live = useLive();

  const rows = positions.data ?? [];
  const tokens = useMemo(
    () => [...new Set(rows.filter((p) => !p.closedAt).map((p) => p.instrumentToken))],
    [rows],
  );
  useWatch(tokens);

  const [closing, setClosing] = useState<Position | null>(null);

  const open = rows.filter((p) => !p.closedAt);
  const unrealised = open.reduce((sum, p) => {
    const last = live.quote(p.instrumentToken)?.last ?? p.lastPrice ?? p.entryPrice;
    return sum + (last - p.entryPrice) * p.quantity;
  }, 0);
  const realised = rows
    .filter((p) => p.closedAt)
    .reduce((sum, p) => sum + (p.realised ?? 0), 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Positions</h1>
          <p>
            Open positions are priced from the live feed. Every figure is gross of
            brokerage and taxes.
          </p>
        </div>
        <div className="page-actions">
          <div className="chip-group">
            <button className={openOnly ? "on" : ""} onClick={() => setOpenOnly(true)}>
              Open
            </button>
            <button className={!openOnly ? "on" : ""} onClick={() => setOpenOnly(false)}>
              Everything
            </button>
          </div>
        </div>
      </div>

      <div className="grid cols-3 mb">
        <div className="stat">
          <span className="k">Open</span>
          <span className="v">{open.length}</span>
          <span className="note">{rupees(open.reduce((s, p) => s + p.entryPrice * p.quantity, 0))} in</span>
        </div>
        <div className="stat">
          <span className="k">Unrealised</span>
          <span className={`v ${toneOf(unrealised)}`}>{signedRupees(unrealised)}</span>
          <span className="note">marked to the live price</span>
        </div>
        <div className="stat">
          <span className="k">Realised {openOnly ? "" : "(shown)"}</span>
          <span className={`v ${toneOf(realised)}`}>{signedRupees(realised)}</span>
          <span className="note">closed round trips</span>
        </div>
      </div>

      <div className="card">
        {rows.length === 0 ? (
          <div className="empty">
            <strong>{openOnly ? "Nothing open" : "No positions yet"}</strong>
            {openOnly
              ? "Switch to Everything to see what has been closed."
              : "A position appears the moment a strategy opens one."}
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Opened</th>
                  <th className="num">Qty</th>
                  <th className="num">Entry</th>
                  <th className="num">{openOnly ? "Last" : "Exit"}</th>
                  <th className="num">P&amp;L</th>
                  <th className="num">Stop / Target</th>
                  <th>Mode</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => {
                  const isOpen = !p.closedAt;
                  const last = live.quote(p.instrumentToken)?.last ?? p.lastPrice ?? p.entryPrice;
                  const pnl = isOpen ? (last - p.entryPrice) * p.quantity : (p.realised ?? 0);
                  const movePct = isOpen
                    ? ((last - p.entryPrice) / p.entryPrice) * 100
                    : p.exitPrice
                      ? ((p.exitPrice - p.entryPrice) / p.entryPrice) * 100
                      : 0;
                  return (
                    <tr key={p.id}>
                      <td>
                        <div className="sym">{p.tradingsymbol}</div>
                        <div className="sub">{p.exchange}</div>
                      </td>
                      <td className="small faint">
                        {clockTime(p.openedAt)}
                        <div className="sub">{p.openedOn}</div>
                      </td>
                      <td className="num">{p.quantity}</td>
                      <td className="num">{fmtPrice(p.entryPrice)}</td>
                      {isOpen ? (
                        <LivePriceCell token={p.instrumentToken} fallback={p.lastPrice} />
                      ) : (
                        <td className="num">
                          {fmtPrice(p.exitPrice)}
                          <div className="sub">
                            {p.exitReason ? (EXIT_CAPTION[p.exitReason] ?? p.exitReason) : ""}
                          </div>
                        </td>
                      )}
                      <td className={`num ${toneOf(pnl)}`}>
                        {signedRupees(pnl, 0)}
                        <div className="sub">{pct(movePct)}</div>
                      </td>
                      <td className="num small faint">
                        {p.stopPrice ? fmtPrice(p.stopPrice) : "—"} /{" "}
                        {p.targetPrice ? fmtPrice(p.targetPrice) : "—"}
                      </td>
                      <td><span className={`pill ${p.mode}`}>{p.mode}</span></td>
                      <td>
                        {isOpen && (
                          <button className="btn sm" onClick={() => setClosing(p)}>
                            Close
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {closing && <CloseDialog position={closing} onClose={() => setClosing(null)} />}
    </>
  );
}

function CloseDialog({ position, onClose }: { position: Position; onClose: () => void }) {
  const exit = useExitPosition();
  const toast = useToast();
  const live = useLive();
  const last = live.quote(position.instrumentToken)?.last ?? position.lastPrice;
  const pnl = last ? (last - position.entryPrice) * position.quantity : null;

  return (
    <Modal
      title={`Close ${position.tradingsymbol}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn danger"
            disabled={exit.isPending}
            onClick={() =>
              exit.mutate(
                { id: position.id, price: null },
                {
                  onSuccess: () => {
                    toast.ok(`${position.tradingsymbol} closed.`);
                    onClose();
                  },
                  onError: (e) => toast.fail(e),
                },
              )
            }
          >
            {exit.isPending ? <span className="spinner" /> : "Close it"}
          </button>
        </>
      }
    >
      <div className="col" style={{ gap: 0 }}>
        <div className="switch-row">
          <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>Quantity</div>
          <div className="num" style={{ fontWeight: 600 }}>{position.quantity}</div>
        </div>
        <div className="switch-row">
          <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>Entry</div>
          <div className="num" style={{ fontWeight: 600 }}>{fmtPrice(position.entryPrice)}</div>
        </div>
        <div className="switch-row">
          <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>Last price</div>
          <div className="num" style={{ fontWeight: 600 }}>{fmtPrice(last)}</div>
        </div>
        <div className="switch-row">
          <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>
            P&amp;L if closed here
          </div>
          <div className={`num ${toneOf(pnl)}`} style={{ fontWeight: 600 }}>
            {signedRupees(pnl)}
          </div>
        </div>
      </div>

      <p className="small muted mt">
        {position.mode === "live"
          ? "A market order goes to Zerodha now. The price you actually get is the price the market gives."
          : "This is a paper position — it is closed at the last price and nothing is sent anywhere."}
      </p>
      <p className="tiny faint">Opened {clockFromMs(new Date(position.openedAt).getTime())}.</p>
    </Modal>
  );
}
